import interstellarApi from './interstellarApi';

export type RingStrategyName =
  | 'round_robin'
  | 'fixed_order'
  | 'longest_idle_agent'
  | 'ring_all'
  | 'random'
  | 'agent_with_least_talk_time'
  | 'agent_with_fewest_calls';

/** All possible ring strategy names that can be activated */
export const ALL_RING_STRATEGY_NAMES: RingStrategyName[] = [
  'round_robin',
  'fixed_order',
  'longest_idle_agent',
  'ring_all',
  'random',
  'agent_with_least_talk_time',
  'agent_with_fewest_calls',
];

/** Descriptions for each ring strategy */
export const RING_STRATEGY_DESCRIPTIONS: Record<RingStrategyName, string> = {
  round_robin:
    'Distribuye las llamadas de forma equitativa entre todos los agentes disponibles, rotando en cada llamada.',
  fixed_order:
    'Siempre intenta primero con el agente 1, luego el 2, etc. Útil cuando hay un agente principal.',
  longest_idle_agent:
    'Llama al agente que lleva más tiempo sin recibir llamadas. Optimiza la distribución de carga.',
  ring_all:
    'Timbra a todos los agentes disponibles simultáneamente. El primero en contestar toma la llamada.',
  random:
    'Selecciona un agente al azar entre los disponibles. Distribución impredecible pero equitativa a largo plazo.',
  agent_with_least_talk_time:
    'Llama al agente con menos tiempo total hablado. Balancea la carga por tiempo de conversación.',
  agent_with_fewest_calls:
    'Llama al agente que ha atendido menos llamadas. Balancea la carga por cantidad de llamadas.',
};

export interface RingStrategy {
  id: number;
  name: RingStrategyName;
  created_at?: string | null;
  modified_at?: string | null;
}

interface RingStrategiesListResponse {
  total_count: number;
  page: number;
  page_size: number;
  ring_strategies: RingStrategy[];
}

interface CreateRingStrategyResponse {
  id: number;
  name: string;
  message: string;
}

interface DeleteRingStrategyResponse {
  id: number;
  message: string;
}

/**
 * Lista estrategias de ring activas: `GET /incoming-call-config/ring-strategy`
 */
export const fetchRingStrategies = async (): Promise<RingStrategy[]> => {
  const response = await interstellarApi.get<RingStrategiesListResponse>(
    '/incoming-call-config/ring-strategy'
  );
  return response.data.ring_strategies ?? [];
};

/**
 * Obtiene detalle de una estrategia: `GET /incoming-call-config/ring-strategy/{strategyId}`
 */
export const fetchRingStrategy = async (strategyId: number): Promise<RingStrategy> => {
  const response = await interstellarApi.get<RingStrategy>(
    `/incoming-call-config/ring-strategy/${strategyId}`
  );
  return response.data;
};

/**
 * Activa una estrategia de ring: `POST /incoming-call-config/ring-strategy`
 */
export const activateRingStrategy = async (
  name: RingStrategyName
): Promise<CreateRingStrategyResponse> => {
  const response = await interstellarApi.post<CreateRingStrategyResponse>(
    '/incoming-call-config/ring-strategy',
    null,
    { params: { name } }
  );
  return response.data;
};

/**
 * Desactiva una estrategia de ring: `DELETE /incoming-call-config/ring-strategy/{strategyId}`
 */
export const deactivateRingStrategy = async (
  strategyId: number
): Promise<DeleteRingStrategyResponse> => {
  const response = await interstellarApi.delete<DeleteRingStrategyResponse>(
    `/incoming-call-config/ring-strategy/${strategyId}`
  );
  return response.data;
};
